import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardList, Package, Home as HomeIcon, User, ChevronLeft, LogOut } from "lucide-react";
import { AppDispatch, RootState } from "../../redux/store";
import { logout } from "../../redux/authSlice";
import { listenToHostOrders } from "../../redux/ordersSlice";
import { listenToOwnerProductsFeed } from "../../redux/productSlice";
import OwnerOrders from "./OwnerOrders";
import ProductsPage from "./Product";

/* ─────────────────────────────────────────────────────────────
   OwnerDashboard: glossy 3D white, black and red shell.
   Desktop  -> floating glossy sidebar with a round open/close button (same look as Profile)
   Mobile   -> floating glossy bottom tab bar with bead icons
   Profile  -> /profile/:userId
   ───────────────────────────────────────────────────────────── */

type TabKey = "orders" | "products";
type NavKey = "home" | TabKey | "profile";

interface NavItem {
  key: NavKey;
  label: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

const NAV: NavItem[] = [
  { key: "home", label: "Home", Icon: HomeIcon },
  { key: "orders", label: "Orders", Icon: ClipboardList },
  { key: "products", label: "Products", Icon: Package },
  { key: "profile", label: "Profile", Icon: User },
];

const TITLES: Record<TabKey, { h: string; p: string }> = {
  orders: { h: "Orders", p: "Everything your customers have asked for." },
  products: { h: "Products", p: "Your products, photos and stock." },
};

const NavButton: React.FC<{ n: NavItem; variant: "side" | "tab"; on: boolean; onPress: (k: NavKey) => void }> = ({ n, variant, on, onPress }) => (
  <button
    className={`${variant === "side" ? "od-nav" : "od-tab"} ${on ? "on" : ""}`}
    data-label={n.label}
    onClick={() => onPress(n.key)}
    aria-current={on ? "page" : undefined}
    aria-label={n.label}
  >
    <span className="od-ico"><n.Icon size={19} strokeWidth={1.8} /></span>
    <span className="od-label">{n.label}</span>
  </button>
);

export default function OwnerDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const user = useSelector((s: RootState) => s.auth.user) as any;

  const tab: TabKey = params.get("tab") === "products" ? "products" : "orders";
  const userId: string | undefined = user?.userId ?? user?.uid ?? user?.id;

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("od-collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("od-collapsed", collapsed ? "1" : "0"); } catch { /* storage unavailable */ }
  }, [collapsed]);

  /* listeners live here, so switching tabs never tears them down and re-creates them */
  useEffect(() => {
    if (!userId) return;
    const unsubOrders = dispatch(listenToHostOrders());
    const unsubProducts = dispatch(listenToOwnerProductsFeed());
    return () => { unsubOrders(); unsubProducts(); };
  }, [dispatch, userId]);

  const goProfile = () => navigate(userId ? `/profile/${userId}` : "/auth");
  const press = (k: NavKey) => {
    if (k === "home") navigate("/");
    else if (k === "profile") goProfile();
    else setParams({ tab: k }, { replace: true });
  };

  const initial = (user?.firstName || user?.email || "?").charAt(0).toUpperCase();
  const name = user?.firstName || user?.email || "Owner";

  return (
    <div className={`od ${collapsed ? "od-collapsed" : ""}`}>
      <style>{OD_CSS}</style>

      {/* ───────── desktop: glossy floating sidebar ───────── */}
      <aside className="od-side" aria-label="Owner navigation">
        <div className="od-brand" onClick={() => navigate("/")} role="button" aria-label="Home">
          <span className="od-spark">✦</span>
          {!collapsed && <span className="od-brand-text">OWNER</span>}
        </div>

        <nav className="od-navlist">
          {NAV.map((n) => <NavButton key={n.key} n={n} variant="side" on={n.key === tab} onPress={press} />)}
        </nav>

        <div className="od-side-foot">
          <button className="od-user" onClick={goProfile} title="Your profile" data-label="Profile">
            <span className="od-avatar">{initial}</span>
            {!collapsed && <span className="od-user-name">{name}</span>}
          </button>
          <button className="od-logout" onClick={() => dispatch(logout())} aria-label="Log out" data-label="Log out">
            <LogOut size={16} strokeWidth={1.8} />{!collapsed && <span>Log out</span>}
          </button>
        </div>

        <button className="od-toggle" onClick={() => setCollapsed((v) => !v)} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!collapsed}>
          <ChevronLeft size={17} strokeWidth={2.2} className="od-toggle-ico" />
        </button>
      </aside>

      {/* ───────── main ───────── */}
      <div className="od-main">
        <header className="od-top">
          <div className="od-title">
            <h1>{TITLES[tab].h}<span className="od-title-spark">✦</span></h1>
            <p>{TITLES[tab].p}</p>
          </div>
          <button className="od-avatar od-avatar-top" onClick={goProfile} aria-label="Profile">{initial}</button>
        </header>

        <main className="od-panel">
          <div className="od-pop" key={tab}>
            {tab === "orders" ? <OwnerOrders /> : <ProductsPage />}
          </div>
        </main>
      </div>

      {/* ───────── mobile: glossy floating tab bar ───────── */}
      <nav className="od-tabs" aria-label="Owner tabs">
        {NAV.map((n) => <NavButton key={n.key} n={n} variant="tab" on={n.key === tab} onPress={press} />)}
      </nav>
    </div>
  );
}

/* ─────────────────────────── styles ─────────────────────────── */
const OD_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');

.od{
  --ink:#000; --muted:#5C5C5C; --line:#E5E5E5; --red:#E10000; --red-d:#B30000;
  /* reusable glossy surfaces */
  --gloss-white:linear-gradient(180deg,#ffffff 0%,#f7f7f7 48%,#ececec 100%);
  --gloss-black:linear-gradient(180deg,#3a3a3a 0%,#111 46%,#000 100%);
  --sphere-white:radial-gradient(circle at 30% 24%,#ffffff 0,#fafafa 22%,#e9e9e9 62%,#cfcfcf 100%);
  --sphere-black:radial-gradient(circle at 30% 24%,#6b6b6b 0,#222 38%,#000 100%);
  --sphere-red:radial-gradient(circle at 30% 24%,#ff9a9a 0,#ff3a3a 28%,#E10000 58%,#8a0000 100%);
  position:relative;display:flex;min-height:100vh;color:var(--ink);font-family:'Jost',system-ui,sans-serif;isolation:isolate;
  background:linear-gradient(180deg,#ffffff 0%,#f4f4f4 100%);
}
.od *{box-sizing:border-box}
.od button{font-family:inherit;cursor:pointer;border:none;background:none;color:inherit}
.od button:focus-visible{outline:2px solid var(--red);outline-offset:3px}

/* ═════════ SIDEBAR: floating glossy slab with double border ═════════ */
.od-side{
  position:sticky;top:14px;z-index:20;flex:none;width:268px;height:calc(100vh - 28px);margin:14px 0 14px 14px;
  display:flex;flex-direction:column;gap:18px;padding:22px 14px;
  background:var(--gloss-white);
  border:1px solid var(--ink);border-radius:30px;
  box-shadow:
    inset 0 2px 0 #fff,
    inset 0 -10px 18px -10px rgba(0,0,0,.12),
    0 1px 0 rgba(255,255,255,.9),
    0 18px 30px -14px rgba(0,0,0,.35),
    0 40px 60px -30px rgba(0,0,0,.35);
  transition:width .35s cubic-bezier(.22,.8,.26,1);
}
.od-side::after{content:"";position:absolute;inset:4px;border:1px solid var(--ink);border-radius:26px;pointer-events:none}
/* glossy sheen across the top */
.od-side::before{content:"";position:absolute;left:6px;right:6px;top:6px;height:34%;border-radius:24px 24px 60% 60% / 24px 24px 22% 22%;
  background:linear-gradient(180deg,rgba(255,255,255,.95),rgba(255,255,255,0));pointer-events:none;z-index:0}
.od-side > *{position:relative;z-index:1}
.od-collapsed .od-side{width:92px;align-items:center;padding-left:10px;padding-right:10px}

/* brand */
.od-brand{display:flex;align-items:center;gap:10px;cursor:pointer;padding:4px 12px;min-height:44px}
.od-spark{color:var(--red);font-size:18px;line-height:1;text-shadow:0 1px 2px rgba(225,0,0,.35)}
.od-brand-text{font-family:'Cormorant Garamond',serif;font-weight:600;font-size:22px;letter-spacing:.4em;transition:color .2s}
.od-brand:hover .od-brand-text{color:var(--red)}
.od-collapsed .od-brand{padding:4px 0;justify-content:center}

/* nav list */
.od-navlist{display:flex;flex-direction:column;gap:8px;margin-top:6px;width:100%}

/* bead icon (same idea as Profile) */
.od-ico{
  flex:none;width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  border:1px solid var(--ink);color:var(--ink);background:var(--sphere-white);
  box-shadow:inset -3px -4px 7px rgba(0,0,0,.16), inset 2px 2px 3px rgba(255,255,255,.9), 0 4px 7px -2px rgba(0,0,0,.35);
  transition:background .25s,color .25s,border-color .25s,transform .25s,box-shadow .25s;
}

.od-nav{
  position:relative;display:flex;align-items:center;gap:12px;width:100%;height:58px;padding:0 18px 0 6px;border-radius:999px;
  color:var(--ink);font-size:12px;font-weight:500;letter-spacing:.22em;text-transform:uppercase;text-align:left;
  transition:background .25s,color .25s,box-shadow .25s,transform .25s;
}
.od-nav:hover{color:var(--red);background:var(--gloss-white);
  box-shadow:inset 0 1px 0 #fff, inset 0 -2px 4px rgba(0,0,0,.06), 0 8px 16px -10px rgba(0,0,0,.4);transform:translateY(-1px)}
.od-nav:hover .od-ico{border-color:var(--red);color:var(--red)}
.od-nav:active{color:var(--red-d);transform:translateY(0)}

/* active = glossy black pill + red glossy sphere */
.od-nav.on{color:#fff;background:var(--gloss-black);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.4), inset 0 -4px 8px rgba(0,0,0,.65), 0 12px 20px -10px rgba(0,0,0,.65)}
.od-nav.on::before{content:"";position:absolute;left:8px;right:8px;top:2px;height:46%;border-radius:999px 999px 60% 60%;
  background:linear-gradient(180deg,rgba(255,255,255,.28),rgba(255,255,255,0));pointer-events:none}
.od-nav.on .od-ico{background:var(--sphere-red);border-color:var(--red);color:#fff;
  box-shadow:inset -3px -4px 7px rgba(0,0,0,.35), inset 2px 2px 3px rgba(255,255,255,.55), 0 4px 8px -2px rgba(225,0,0,.6)}
.od-nav.on:hover{color:#fff;transform:translateY(-1px);background:linear-gradient(180deg,#4a4a4a 0%,#1c1c1c 46%,#050505 100%)}
.od-nav.on:hover .od-ico{color:#fff;border-color:var(--red)}
.od-label{white-space:nowrap}

/* collapsed: bead-only buttons */
.od-collapsed .od-nav{width:58px;padding:0;justify-content:center}
.od-collapsed .od-nav .od-ico{width:44px;height:44px}
.od-collapsed .od-label{display:none}

/* tooltips when collapsed */
.od-collapsed .od-nav::after,.od-collapsed .od-user::after,.od-collapsed .od-logout::after{
  content:attr(data-label);position:absolute;left:calc(100% + 16px);top:50%;transform:translateY(-50%) scale(.9);opacity:0;pointer-events:none;z-index:50;
  background:var(--gloss-black);color:#fff;font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;
  padding:8px 16px;border-radius:999px;white-space:nowrap;box-shadow:inset 0 1px 0 rgba(255,255,255,.35),0 10px 18px -8px rgba(0,0,0,.6);transition:.15s}
.od-collapsed .od-nav:hover::after,.od-collapsed .od-user:hover::after,.od-collapsed .od-logout:hover::after{opacity:1;transform:translateY(-50%) scale(1)}

/* footer */
.od-side-foot{margin-top:auto;display:flex;flex-direction:column;gap:12px;width:100%}
.od-user,.od-logout{position:relative;display:flex;align-items:center;gap:12px;width:100%;border-radius:999px;font-weight:500;color:var(--ink);transition:color .2s,border-color .2s,background .2s,box-shadow .2s,transform .2s}
.od-user{font-size:14px;letter-spacing:.04em;padding:4px 10px 4px 4px}
.od-user:hover{color:var(--red)}
.od-user:hover .od-avatar{background:var(--sphere-red);border-color:var(--red)}
.od-user-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.od-logout{justify-content:center;height:46px;border:1px solid var(--ink);font-size:11px;letter-spacing:.22em;text-transform:uppercase;gap:8px;
  background:var(--gloss-white);box-shadow:inset 0 1px 0 #fff,inset 0 -3px 6px rgba(0,0,0,.08),0 6px 12px -8px rgba(0,0,0,.45)}
.od-logout:hover{color:#fff;border-color:var(--red);background:linear-gradient(180deg,#ff4a4a 0%,#E10000 50%,#a00000 100%);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.5),0 10px 18px -8px rgba(225,0,0,.6);transform:translateY(-1px)}
.od-collapsed .od-user{width:58px;height:58px;padding:0;justify-content:center}
.od-collapsed .od-logout{width:58px;height:46px;padding:0}

/* glossy black sphere avatar */
.od-avatar{flex:none;width:44px;height:44px;border-radius:50%;display:grid;place-items:center;border:1px solid var(--ink);color:#fff;font-weight:500;
  font-family:'Cormorant Garamond',serif;font-size:20px;background:var(--sphere-black);
  box-shadow:inset -3px -4px 7px rgba(0,0,0,.5), inset 2px 2px 3px rgba(255,255,255,.35), 0 4px 8px -2px rgba(0,0,0,.5);
  transition:background .2s,border-color .2s}

/* ═════════ OPEN / CLOSE BUTTON: round, double ring, glossy (like Profile) ═════════ */
.od-toggle{
  position:absolute;top:30px;right:-21px;z-index:5;width:40px;height:40px;border-radius:50%;
  display:grid;place-items:center;color:var(--ink);border:1px solid var(--ink);background:var(--sphere-white);
  box-shadow:0 0 0 3px #fff, 0 0 0 4px var(--ink), inset -3px -4px 7px rgba(0,0,0,.16), inset 2px 2px 3px rgba(255,255,255,.9), 0 8px 14px -4px rgba(0,0,0,.45);
  transition:background .25s,color .25s,border-color .25s,box-shadow .25s,transform .25s;
}
.od-toggle:hover{color:#fff;border-color:var(--red);background:var(--sphere-red);
  box-shadow:0 0 0 3px #fff, 0 0 0 4px var(--red), inset -3px -4px 7px rgba(0,0,0,.3), inset 2px 2px 3px rgba(255,255,255,.55), 0 8px 14px -4px rgba(225,0,0,.55);transform:scale(1.06)}
.od-toggle:active{transform:scale(.96)}
.od-toggle-ico{transition:transform .35s cubic-bezier(.22,.8,.26,1)}
.od-collapsed .od-toggle-ico{transform:rotate(180deg)}

/* ═════════ MAIN ═════════ */
.od-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:18px;padding:28px clamp(14px,3vw,36px) 36px}
.od-top{display:flex;align-items:center;gap:16px}
.od-title{flex:1;min-width:0}
.od-title h1{margin:0;font-family:'Cormorant Garamond',serif;font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.05;letter-spacing:.01em;color:var(--ink)}
.od-title-spark{color:var(--red);font-size:.4em;vertical-align:super;margin-left:6px}
.od-title p{margin:6px 0 0;color:var(--muted);font-size:14px;letter-spacing:.04em}
.od-avatar-top{display:none;cursor:pointer}
.od-avatar-top:hover{background:var(--sphere-red);border-color:var(--red)}
.od-panel{position:relative;flex:1;border-radius:22px;padding:clamp(8px,1.5vw,16px);background:#fff;border:1px solid var(--ink);min-height:60vh;overflow:hidden;
  box-shadow:inset 0 2px 0 #fff, 0 18px 34px -22px rgba(0,0,0,.35)}
.od-panel::after{content:"";position:absolute;inset:4px;border:1px solid var(--ink);border-radius:18px;pointer-events:none;opacity:.12}
.od-pop{position:relative;animation:od-pop .3s ease-out both}
@keyframes od-pop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}

/* ═════════ MOBILE: floating glossy tab bar ═════════ */
.od-tabs{display:none}
@media (max-width:899px){
  .od-side{display:none}
  .od-main{padding:18px 14px 130px;gap:14px}
  .od-title p{display:none}
  .od-avatar-top{display:grid}
  .od-panel{border-radius:18px}
  .od-tabs{position:fixed;z-index:30;left:12px;right:12px;bottom:calc(12px + env(safe-area-inset-bottom));display:grid;grid-template-columns:repeat(4,1fr);gap:4px;
    padding:10px 8px;background:var(--gloss-white);border:1px solid var(--ink);border-radius:30px;
    box-shadow:inset 0 2px 0 #fff, inset 0 -8px 14px -10px rgba(0,0,0,.15), 0 20px 30px -12px rgba(0,0,0,.45)}
  .od-tabs::after{content:"";position:absolute;inset:4px;border:1px solid var(--ink);border-radius:26px;pointer-events:none}
  .od-tab{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;gap:5px;padding:2px 0;color:var(--muted);font-weight:500;font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;
    -webkit-tap-highlight-color:transparent;transition:color .2s}
  .od-tab .od-ico{width:42px;height:42px}
  .od-tab:hover{color:var(--red)}
  .od-tab:hover .od-ico{border-color:var(--red);color:var(--red)}
  .od-tab:active .od-ico{border-color:var(--red-d);color:var(--red-d);transform:scale(.94)}
  .od-tab.on{color:var(--ink)}
  .od-tab.on .od-ico{background:var(--sphere-red);border-color:var(--red);color:#fff;
    box-shadow:inset -3px -4px 7px rgba(0,0,0,.35), inset 2px 2px 3px rgba(255,255,255,.55), 0 4px 8px -2px rgba(225,0,0,.6)}
  .od-tab.on:hover .od-ico{color:#fff}
}
@media (prefers-reduced-motion:reduce){
  .od *{animation:none !important;transition:none !important}
}
`;